import React, { useState, useEffect, useRef } from 'react';
import { DB, uploadImage } from '../data/database';
import { resolveTargetZone, getZoneAllocations, getYardLocations, getDestinationCountries, POOL_ZONE } from '../data/constants';
import { logUnitEvent } from '../data/unitEvents';
import DetailModal from './modals/DetailModal';

const MIN_PHOTOS = 2;

export default function YardMap({ user }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const h = (e) => { if (e.key === 'bigstar_vehicles') setTick(t => t + 1); };
    window.addEventListener('storage', h);
    return () => window.removeEventListener('storage', h);
  }, []);

  const [moveUnit, setMoveUnit] = useState(null);
  const [moveStep, setMoveStep] = useState(1);
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [zoneDrawer, setZoneDrawer] = useState(null);
  const [drawerTab, setDrawerTab] = useState('both');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerUnit, setDrawerUnit] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [detailUnit, setDetailUnit] = useState(null);

  const openZoneDrawer = (letter, country, tab) => {
    setZoneDrawer({ letter, country });
    setDrawerTab(tab || 'both');
    setDrawerUnit(null);
  };
  const closeDrawer = () => setZoneDrawer(null);

  useEffect(() => {
    if (!zoneDrawer) { setDrawerOpen(false); setDrawerUnit(null); return; }
    const raf = requestAnimationFrame(() => setDrawerOpen(true));
    return () => cancelAnimationFrame(raf);
  }, [zoneDrawer]);

  useEffect(() => {
    if (!zoneDrawer) return;
    const h = (e) => { if (e.key === 'Escape') { if (lightboxSrc) setLightboxSrc(null); else closeDrawer(); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [zoneDrawer, lightboxSrc]);

  const yardId = user.yard || 'hokkaido_yard';
  const yardMeta = getYardLocations().find(y => y.id === yardId);
  const yardName = yardMeta ? yardMeta.name : yardId;

  const allUnits = (DB.get('vehicles') || []).filter(u => u.current_zone && u.destination_yard === yardId);
  const pool = allUnits.filter(u => u.current_zone === POOL_ZONE);
  const allocations = getZoneAllocations(yardId);
  const sortedLetters = Object.keys(allocations).sort();

  const countryMeta = (id) => getDestinationCountries().find(c => c.id === id) || { name: id, flag: '🌍' };
  const unitsInZone = (code) => allUnits.filter(u => u.current_zone === code);

  const targetChip = (unit) => {
    const t = resolveTargetZone(unit);
    if (t.status === 'ok') return { label: `→ ${t.zone}`, color: 'var(--cyan)', canMove: true, zone: t.zone };
    const reasons = {
      no_yard: 'no yard',
      no_country: 'awaiting country',
      unmapped: 'unmapped',
      movable_unknown: 'movable unknown',
    };
    return { label: reasons[t.status] || '—', color: 'var(--text3)', canMove: false, zone: null };
  };

  const openMove = (unit) => {
    setMoveUnit(unit);
    setMoveStep(1);
    setBeforePhotos([]);
    setAfterPhotos([]);
    setSaving(false);
  };

  const closeMove = () => {
    if (saving) return;
    setMoveUnit(null);
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Yard Map</h2>
        <span style={{
          padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
          background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border)',
        }}>
          {yardMeta?.flag || '📍'} {yardName}
        </span>
      </div>

      {/* THE POOL */}
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
        marginBottom: 24, overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: '1px solid var(--border)',
          background: 'color-mix(in srgb, var(--cyan) 6%, var(--bg2))',
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--cyan)', letterSpacing: 1 }}>THE POOL</span>
          <span style={{
            padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
            background: 'color-mix(in srgb, var(--cyan) 15%, transparent)', color: 'var(--cyan)',
          }}>
            {pool.length}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 4 }}>inspected, awaiting move to zone</span>
        </div>

        {pool.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            No units in the pool
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 10, padding: 16,
          }}>
            {pool.map(unit => {
              const chip = targetChip(unit);
              return (
                <div key={unit.id} style={{
                  background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10,
                  padding: 12, display: 'flex', flexDirection: 'column', minHeight: 100,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                    {unit.make} {unit.model}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
                    {unit.stockNumber || unit.sku || '—'}
                  </div>
                  <div style={{ flex: 1 }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                      background: `color-mix(in srgb, ${chip.color} 12%, transparent)`,
                      color: chip.color,
                    }}>
                      {chip.label}
                    </span>
                    {chip.canMove && (
                      <button
                        onClick={() => openMove(unit)}
                        style={{
                          marginLeft: 'auto', padding: '3px 10px', borderRadius: 6,
                          fontSize: 10, fontWeight: 600, cursor: 'pointer',
                          background: 'color-mix(in srgb, var(--cyan) 15%, var(--bg4))',
                          border: '1px solid var(--cyan)',
                          color: 'var(--cyan)',
                        }}
                      >
                        Move →
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ZONE PLOTS */}
      {sortedLetters.length === 0 ? (
        <div style={{
          padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13,
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
        }}>
          No zones allocated for this yard
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 16,
        }}>
          {sortedLetters.map(letter => {
            const countryId = allocations[letter];
            const meta = countryMeta(countryId);
            const bay1 = unitsInZone(letter + '1');
            const bay2 = unitsInZone(letter + '2');
            const total = bay1.length + bay2.length;

            return (
              <div key={letter}
                onClick={() => openZoneDrawer(letter, meta, 'both')}
                style={{
                  background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
                  overflow: 'hidden', cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.25)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
              >
                {/* Plot header */}
                <div
                  style={{
                    padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10,
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <span style={{ fontSize: 18 }}>{meta.flag}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{meta.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>ZONE {letter}</div>
                  </div>
                  <span style={{
                    padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                    background: 'var(--bg3)', color: 'var(--text2)',
                  }}>
                    {total}
                  </span>
                </div>

                {/* Two bays */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                  {/* Bay 1 — Movable */}
                  <div
                    style={{
                      padding: 16, cursor: 'pointer',
                      background: 'color-mix(in srgb, var(--green) 5%, var(--bg2))',
                      borderRight: '1px solid var(--border)',
                      transition: 'background 0.15s',
                    }}
                    onClick={e => { e.stopPropagation(); openZoneDrawer(letter, meta, '1'); }}
                    onMouseEnter={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--green) 10%, var(--bg2))'}
                    onMouseLeave={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--green) 5%, var(--bg2))'}
                  >
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--green)', marginBottom: 4 }}>
                      {bay1.length}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--green)', opacity: 0.8 }}>
                      {letter}1 · Movable
                    </div>
                  </div>

                  {/* Bay 2 — Not movable */}
                  <div
                    style={{
                      padding: 16, cursor: 'pointer',
                      background: 'color-mix(in srgb, var(--orange) 5%, var(--bg2))',
                      transition: 'background 0.15s',
                    }}
                    onClick={e => { e.stopPropagation(); openZoneDrawer(letter, meta, '2'); }}
                    onMouseEnter={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--orange) 10%, var(--bg2))'}
                    onMouseLeave={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--orange) 5%, var(--bg2))'}
                  >
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--orange)', marginBottom: 4 }}>
                      {bay2.length}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--orange)', opacity: 0.8 }}>
                      {letter}2 · Not movable
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Move Modal */}
      {moveUnit && <MoveModal unit={moveUnit} user={user} step={moveStep} setStep={setMoveStep}
        beforePhotos={beforePhotos} setBeforePhotos={setBeforePhotos}
        afterPhotos={afterPhotos} setAfterPhotos={setAfterPhotos}
        saving={saving} setSaving={setSaving}
        onClose={closeMove} />}

      {/* Zone Drawer */}
      {zoneDrawer && !detailUnit && (() => {
        const L = zoneDrawer.letter;
        const bay1Units = unitsInZone(L + '1');
        const bay2Units = unitsInZone(L + '2');
        const totalUnits = bay1Units.length + bay2Units.length;

        const unitTags = (u) => {
          const tags = [];
          if (u.year) tags.push({ text: String(u.year), bg: 'var(--bg3)', color: 'var(--text2)' });
          if (u.engine_starts === true) tags.push({ text: 'ENGINE ✓', bg: 'color-mix(in srgb, var(--green) 12%, transparent)', color: 'var(--green)' });
          if (u.engine_starts === false) tags.push({ text: 'NO START', bg: 'color-mix(in srgb, var(--orange) 12%, transparent)', color: 'var(--orange)' });
          if (u.accidented) tags.push({ text: 'ACCIDENT', bg: 'color-mix(in srgb, var(--red) 12%, transparent)', color: 'var(--red)' });
          return tags;
        };

        const UnitCard = ({ unit }) => (
          <div
            onClick={() => setDrawerUnit(unit)}
            style={{
              padding: 12, borderRadius: 10, cursor: 'pointer',
              background: 'var(--bg3)', border: '1px solid var(--border)',
              transition: 'background 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg2)'; e.currentTarget.style.borderColor = 'var(--text3)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
              {unit.make} {unit.model}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace', marginBottom: 6 }}>
              {unit.stockNumber || unit.sku || '—'}
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {unitTags(unit).map((t, i) => (
                <span key={i} style={{
                  padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600,
                  background: t.bg, color: t.color,
                }}>{t.text}</span>
              ))}
            </div>
          </div>
        );

        const EmptyBay = () => (
          <div style={{
            padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 12,
            fontStyle: 'italic', border: '1px dashed var(--border)', borderRadius: 10,
          }}>Empty bay</div>
        );

        const BayColumn = ({ code, label, color, units }) => (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span style={{
                padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                fontFamily: 'monospace', background: `color-mix(in srgb, ${color} 12%, transparent)`, color,
              }}>{code}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase' }}>{label}</span>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{units.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {units.length === 0 ? <EmptyBay /> : units.map(u => <UnitCard key={u.id} unit={u} />)}
            </div>
          </div>
        );

        const TABS = [
          { id: 'both', label: 'Both', count: totalUnits, color: 'var(--text2)' },
          { id: '1', label: `${L}1 movable`, count: bay1Units.length, color: 'var(--green)' },
          { id: '2', label: `${L}2 static`, count: bay2Units.length, color: 'var(--orange)' },
        ];

        return (
          <>
            {/* Scrim */}
            <div
              onClick={closeDrawer}
              style={{
                position: 'fixed', inset: 0, zIndex: 900,
                background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
                opacity: drawerOpen ? 1 : 0,
                transition: 'opacity 0.2s ease',
              }}
            />
            {/* Panel */}
            <div
              onClick={e => e.stopPropagation()}
              style={{
                position: 'fixed', top: 0, right: 0, height: '100%', zIndex: 901,
                width: 'min(640px, 96vw)',
                background: 'var(--card)', borderLeft: '1px solid var(--border)',
                boxShadow: '-24px 0 60px -20px rgba(0,0,0,0.6)',
                display: 'flex', flexDirection: 'column',
                transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.26s cubic-bezier(.4,0,.2,1)',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '20px 22px', borderBottom: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
              }}>
                {drawerUnit ? (
                  <>
                    <button
                      onClick={() => setDrawerUnit(null)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--blue)', fontSize: 13, fontWeight: 600, padding: 0,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      ‹ Back to {L}
                    </button>
                    <div style={{ flex: 1 }} />
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: 28 }}>{zoneDrawer.country.flag}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                        {zoneDrawer.country.name}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'monospace', marginTop: 2 }}>
                        ZONE {L}
                      </div>
                    </div>
                  </>
                )}
                <button
                  onClick={closeDrawer}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text3)', fontSize: 24, lineHeight: 1, padding: '4px 8px',
                    borderRadius: 6, flexShrink: 0,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >×</button>
              </div>

              {/* Tabs (hidden when viewing a unit) */}
              {!drawerUnit && (
                <div style={{
                  display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', flexShrink: 0,
                }}>
                  {TABS.map(tab => {
                    const active = drawerTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setDrawerTab(tab.id)}
                        style={{
                          flex: 1, padding: '10px 0', cursor: 'pointer',
                          background: active ? 'color-mix(in srgb, ' + tab.color + ' 8%, var(--bg2))' : 'transparent',
                          border: 'none', borderBottom: active ? '2px solid ' + tab.color : '2px solid transparent',
                          color: active ? tab.color : 'var(--text3)',
                          fontSize: 12, fontWeight: 600,
                          transition: 'all 0.15s',
                        }}
                      >
                        {tab.label} <span style={{ opacity: 0.7 }}>{tab.count}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Body */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {!drawerUnit ? (
                  /* ── TABBED LIST VIEW ── */
                  <div style={{ padding: 16 }}>
                    {drawerTab === 'both' ? (
                      <div style={{ display: 'flex', gap: 16 }} className="yard-drawer-cols">
                        <BayColumn code={L + '1'} label="MOVABLE" color="var(--green)" units={bay1Units} />
                        <BayColumn code={L + '2'} label="NOT MOVABLE" color="var(--orange)" units={bay2Units} />
                      </div>
                    ) : drawerTab === '1' ? (
                      <BayColumn code={L + '1'} label="MOVABLE" color="var(--green)" units={bay1Units} />
                    ) : (
                      <BayColumn code={L + '2'} label="NOT MOVABLE" color="var(--orange)" units={bay2Units} />
                    )}
                  </div>
                ) : (
                  /* ── UNIT VIEW (move history) ── */
                  <div style={{ padding: 22 }}>
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
                        {drawerUnit.make} {drawerUnit.model}{drawerUnit.year ? ` (${drawerUnit.year})` : ''}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'monospace', marginTop: 4 }}>
                        {drawerUnit.stockNumber || drawerUnit.sku || '—'}
                      </div>
                    </div>

                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
                      Move History
                    </div>

                    {(() => {
                      const history = [...(drawerUnit.move_history || [])].reverse();
                      if (history.length === 0) {
                        return (
                          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                            No moves recorded yet.
                          </div>
                        );
                      }
                      return history.map((rec, idx) => (
                        <div key={idx} style={{
                          marginBottom: 16, padding: 16, borderRadius: 10,
                          background: 'var(--bg3)', border: '1px solid var(--border)',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <span style={{
                              padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                              background: 'color-mix(in srgb, var(--cyan) 12%, transparent)', color: 'var(--cyan)',
                            }}>{(rec.from || '?').toUpperCase()}</span>
                            <span style={{ color: 'var(--text3)', fontSize: 12 }}>→</span>
                            <span style={{
                              padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                              background: 'color-mix(in srgb, var(--green) 12%, transparent)', color: 'var(--green)',
                            }}>{(rec.to || '?').toUpperCase()}</span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
                            {rec.moved_by_name || 'Unknown'} · {rec.moved_at ? new Date(rec.moved_at).toLocaleString() : '—'}
                          </div>
                          {rec.before_photos?.length > 0 && (
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>BEFORE</div>
                              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                                {rec.before_photos.map((src, pi) => (
                                  <img key={pi} src={src} alt="" onClick={() => setLightboxSrc(src)} style={{
                                    width: 72, height: 54, borderRadius: 6, objectFit: 'cover',
                                    border: '1px solid var(--border)', cursor: 'pointer', flexShrink: 0,
                                  }} />
                                ))}
                              </div>
                            </div>
                          )}
                          {rec.after_photos?.length > 0 && (
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>AFTER</div>
                              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                                {rec.after_photos.map((src, pi) => (
                                  <img key={pi} src={src} alt="" onClick={() => setLightboxSrc(src)} style={{
                                    width: 72, height: 54, borderRadius: 6, objectFit: 'cover',
                                    border: '1px solid var(--border)', cursor: 'pointer', flexShrink: 0,
                                  }} />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ));
                    })()}

                    <button
                      className="btn btn-secondary"
                      style={{ width: '100%', marginTop: 8 }}
                      onClick={() => setDetailUnit(drawerUnit)}
                    >
                      View full details →
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        );
      })()}

      {/* Photo Lightbox */}
      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.92)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out',
          }}
        >
          <button onClick={() => setLightboxSrc(null)} style={{
            position: 'absolute', top: 16, right: 20,
            background: 'none', border: 'none', color: '#fff', fontSize: 32, cursor: 'pointer',
          }}>×</button>
          <img src={lightboxSrc} alt="" onClick={e => e.stopPropagation()} style={{
            maxWidth: '92vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, cursor: 'default',
          }} />
        </div>
      )}

      {/* Detail Modal (read-only) */}
      {detailUnit && (
        <DetailModal
          item={detailUnit}
          type="vehicles"
          onClose={() => setDetailUnit(null)}
          currency="JPY"
          canPrice={false}
          isCustomer={false}
          user={user}
        />
      )}
    </div>
  );
}

function MoveModal({ unit, user, step, setStep, beforePhotos, setBeforePhotos, afterPhotos, setAfterPhotos, saving, setSaving, onClose }) {
  const beforeFileRef = useRef();
  const beforeCameraRef = useRef();
  const afterFileRef = useRef();
  const afterCameraRef = useRef();

  const target = resolveTargetZone(unit);
  const targetZone = target.zone;
  const countryId = unit.destination_country;
  const meta = getDestinationCountries().find(c => c.id === countryId) || { name: countryId, flag: '🌍' };

  const compressImage = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handleFiles = async (files, setPhotos) => {
    const arr = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!arr.length) return;
    const compressed = await Promise.all(arr.map(f => compressImage(f).catch(() => null)));
    const valid = compressed.filter(Boolean);
    setPhotos(prev => [...prev, ...valid]);
    for (const c of valid) {
      uploadImage(c).then(url => {
        if (url && url !== c) setPhotos(prev => prev.map(src => src === c ? url : src));
      }).catch(() => {});
    }
  };

  const removePhoto = (idx, setPhotos) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const handleFinish = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const record = {
        from: unit.current_zone,
        to: targetZone,
        before_photos: beforePhotos,
        after_photos: afterPhotos,
        moved_at: new Date().toISOString(),
        moved_by: user?.id || null,
        moved_by_name: user?.name || null,
      };

      const list = DB.get('vehicles') || [];
      const updated = list.map(i => i.id === unit.id
        ? { ...i, current_zone: targetZone, move_history: [...(i.move_history || []), record] }
        : i
      );
      const result = await DB.setAsync('vehicles', updated);
      if (result?.error) {
        console.error('[YardMap] Move failed:', result.error);
        alert('Move failed — not saved. Try again.');
        setSaving(false);
        return;
      }

      await logUnitEvent(unit.id, 'vehicles', 'zone_move', {
        from: unit.current_zone, to: targetZone,
        before_photos: beforePhotos, after_photos: afterPhotos,
      }, user);

      onClose();
    } catch (err) {
      console.error('[YardMap] Move error:', err);
      alert('Something went wrong. Try again.');
      setSaving(false);
    }
  };

  const progress = Math.round((step / 3) * 100);

  const photoGrid = (photos, setPhotos, fileRef, cameraRef, label) => (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button className="btn btn-secondary" style={{ fontSize: 12 }}
          onClick={() => cameraRef.current?.click()}>
          📷 Take Photo
        </button>
        <button className="btn btn-secondary" style={{ fontSize: 12 }}
          onClick={() => fileRef.current?.click()}>
          📁 Choose Files
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={e => { handleFiles(e.target.files, setPhotos); e.target.value = ''; }} />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
          onChange={e => { handleFiles(e.target.files, setPhotos); e.target.value = ''; }} />
      </div>
      <div style={{ fontSize: 12, color: photos.length >= MIN_PHOTOS ? 'var(--green)' : 'var(--text3)', marginBottom: 10 }}>
        {photos.length} of min {MIN_PHOTOS} taken
      </div>
      {photos.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
          {photos.map((src, i) => (
            <div key={i} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
              <img src={src} alt="" style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }} />
              <button onClick={() => removePhoto(i, setPhotos)} style={{
                position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%',
                background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', cursor: 'pointer',
                fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 540, display: 'flex', flexDirection: 'column', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
        <div style={{ height: 3, background: 'var(--bg3)' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, var(--blue), var(--cyan))', borderRadius: '0 3px 3px 0', transition: 'width 0.4s ease' }} />
        </div>

        <div className="modal-header">
          <div>
            <h3 className="modal-title" style={{ margin: 0 }}>Move Unit</h3>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
              {unit.make} {unit.model} — {unit.stockNumber || unit.sku || '—'}
            </div>
          </div>
          <button className="modal-close" onClick={onClose} disabled={saving}>×</button>
        </div>

        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', maxHeight: 'none' }}>
          {/* Step 1: Before photos */}
          {step === 1 && (
            <div>
              <h4 style={{ margin: '0 0 6px', fontSize: 15, color: 'var(--text)' }}>Photograph in the pool</h4>
              <p style={{ color: 'var(--text3)', fontSize: 12, margin: '0 0 16px' }}>
                Windshield (with written details) + full car. Minimum {MIN_PHOTOS} photos.
              </p>
              {photoGrid(beforePhotos, setBeforePhotos, beforeFileRef, beforeCameraRef, 'before')}
            </div>
          )}

          {/* Step 2: Move it */}
          {step === 2 && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 15, color: 'var(--text)' }}>Move the car to {targetZone}</h4>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 20px', borderRadius: 10,
                background: 'color-mix(in srgb, var(--cyan) 10%, var(--bg3))',
                border: '1px solid var(--border)', marginBottom: 16,
              }}>
                <span style={{ fontSize: 18 }}>{meta.flag}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{meta.name}</span>
                <span style={{
                  padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                  background: 'color-mix(in srgb, var(--cyan) 15%, transparent)', color: 'var(--cyan)',
                }}>
                  {targetZone}
                </span>
              </div>
              <p style={{ color: 'var(--text3)', fontSize: 12, margin: 0 }}>
                Drive or push the unit to its zone now. Tap "Done moving" when parked.
              </p>
            </div>
          )}

          {/* Step 3: After photos */}
          {step === 3 && (
            <div>
              <h4 style={{ margin: '0 0 6px', fontSize: 15, color: 'var(--text)' }}>Photograph at {targetZone}</h4>
              <p style={{ color: 'var(--text3)', fontSize: 12, margin: '0 0 16px' }}>
                Confirm the unit is parked in {targetZone}. Minimum {MIN_PHOTOS} photos.
              </p>
              {photoGrid(afterPhotos, setAfterPhotos, afterFileRef, afterCameraRef, 'after')}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <button className="btn btn-secondary" disabled={saving}
            onClick={step > 1 ? () => setStep(step - 1) : onClose}>
            {step > 1 ? '← Back' : 'Cancel'}
          </button>

          {step === 1 && (
            <button className="btn btn-primary" disabled={beforePhotos.length < MIN_PHOTOS}
              onClick={() => setStep(2)}>
              Next →
            </button>
          )}
          {step === 2 && (
            <button className="btn btn-primary" onClick={() => setStep(3)}>
              Done moving →
            </button>
          )}
          {step === 3 && (
            <button className="btn btn-success" disabled={afterPhotos.length < MIN_PHOTOS || saving}
              onClick={handleFinish}>
              {saving ? 'Saving…' : 'Finish move ✓'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
